/**
 * LiveStrip - real-time pulse strip with customizable chip catalog.
 *
 * Owns:
 *   - Selected chip ids + per-chip color override (persisted to localStorage)
 *   - The Customize popover state
 *
 * Reads:
 *   - Live counter values from useFakeLive (Phase F replaces with real polling
 *     against GET /api/v1/live/counters; same return shape).
 *
 * Hidden chips: any chip whose value comes back !ok is hidden entirely.
 */
import React, { useEffect, useRef, useState } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useFakeLive } from "./useFakeLive.v2";
import { useViewport } from "./useViewport.v2";

const CATALOG = [
  { id: "today_revenue",     label: "today's call revenue", format: "money", color: "#FC8019" },
  { id: "calls_in_progress", label: "calls in progress", format: "count",  color: "#FC8019" },
  { id: "fans_online",       label: "fans online",       format: "count",  color: "#7C4DFF" },
  { id: "creators_online",   label: "creators online",   format: "count",  color: "#4CAF50" },
  { id: "pending_payouts",   label: "pending payouts",   format: "money",  color: "#FF6B9D" },
  { id: "brand_calls_live",  label: "brand calls live",  format: "count",  color: "#FF6B9D" },
  { id: "fan_calls_live",    label: "fan calls live",    format: "count",  color: "#FC8019" },
  { id: "live_rooms",        label: "live rooms running", format: "count", color: "#FC8019" },
  { id: "brands_online",     label: "brands online",     format: "count",  color: "#4CAF50" },
  { id: "signups_today",     label: "signups today",     format: "count",  color: "#7C4DFF" },
  { id: "refunds_today",     label: "refunds today",     format: "count",  color: "#FF6B9D" },
  { id: "tickets_open",      label: "open tickets",      format: "count",  color: "#FC8019" },
  // Combined "today" counters (added 2026-06-27).
  { id: "total_revenue",     label: "total inflow",      format: "money",  color: "#4CAF50" },
  { id: "wallet_topups",     label: "wallet top-ups (real)", format: "money", color: "#2EB4B0" },
  { id: "fancall_credit",    label: "fancall credit",    format: "money",  color: "#FF6B9D" },
  { id: "total_signups",     label: "total signups",     format: "count",  color: "#7C4DFF" },
  { id: "calls_completed",   label: "calls completed",   format: "count",  color: "#FC8019" },
  { id: "referral_fans",     label: "fans by referral",  format: "count",  color: "#FF6B9D" },
];

const PALETTE = ["#FC8019", "#7C4DFF", "#4CAF50", "#FF6B9D"];
// Default chips are the ones the read-only backend can actually compute. The
// presence/in-call counters need real-time session tracking we don't have, so
// they're available in the catalog but stay out of the default set.
// Default strip shows the COMPLETE counters — total revenue (fans + brands) and
// total signups (fans + creators + brands) — plus calls-completed and
// fans-by-referral. The narrower fan-only "today's revenue" / "signups today"
// are still in the catalog (addable) but dropped from the default set so the
// strip doesn't show a narrow + complete duplicate pair. Each has a hover
// breakdown. The presence/live-call counters need real-time tracking, so they
// stay out of the default set.
const DEFAULTS = ["total_revenue", "wallet_topups", "fancall_credit", "total_signups", "calls_completed", "referral_fans", "refunds_today", "pending_payouts", "tickets_open"];
// Bumped 2026-06-27 (v3 → v4): added the wallet-top-ups tile (money fans added
// to their wallet today — shown separately from revenue).
// Bumped 2026-07-03 (v4 → v5): wallet-top-ups now shows REAL cash only (leak of
// creator-referral rewards removed) and Fancall-granted credit split into its own
// `fancall_credit` tile. Bumping the key resets everyone to this canonical set
// once; re-customise via the pencil.
const STORAGE_KEY = "fancall-livestrip-prefs-v5";
const MAX_CHIPS = 12;

function loadPrefs() {
  try {
    if (typeof window === "undefined") return { chipIds: DEFAULTS.slice(), colors: {} };
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { chipIds: DEFAULTS.slice(), colors: {} };
    const parsed = JSON.parse(raw);
    return {
      chipIds: Array.isArray(parsed.chipIds) ? parsed.chipIds.slice(0, MAX_CHIPS) : DEFAULTS.slice(),
      colors: parsed.colors || {},
    };
  } catch (e) {
    return { chipIds: DEFAULTS.slice(), colors: {} };
  }
}
function savePrefs(prefs) {
  try { if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch (e) { /* ignore */ }
}
function formatValue(v, format) {
  if (v == null) return "..";
  if (format === "money") {
    if (v >= 1e7) return "Rs " + (v / 1e7).toFixed(2) + "Cr";
    if (v >= 1e5) return "Rs " + (v / 1e5).toFixed(2) + "L";
    if (v >= 1e3) return "Rs " + (v / 1e3).toFixed(1) + "K";
    return "Rs " + v;
  }
  return v.toLocaleString("en-IN");
}

function PulseDot({ color }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", width: 10, height: 10, marginRight: 8 }}>
      <span style={{ position: "absolute", inset: 0, backgroundColor: color, borderRadius: "50%", opacity: 0.6, animation: "fc-pulse 1.6s cubic-bezier(0,0,0.2,1) infinite" }} />
      <span style={{ position: "relative", width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
      <style>{`@keyframes fc-pulse { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.6); opacity: 0; } }`}</style>
    </span>
  );
}

function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>
    </svg>
  );
}

function GripIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/>
      <circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/>
      <circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/>
    </svg>
  );
}

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

function ColorSwatches({ value, onChange }) {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {PALETTE.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)} aria-label={"Color " + c} style={{
          width: 16, height: 16, borderRadius: "50%",
          backgroundColor: c, border: "2px solid " + (value === c ? T.text.primary : "transparent"),
          cursor: "pointer", padding: 0, outline: "none",
        }} />
      ))}
    </span>
  );
}

const miniBtn = (disabled) => ({
  width: 22, height: 22, borderRadius: 6,
  backgroundColor: "transparent", border: "1px solid " + T.border.subtle,
  color: T.text.secondary, fontSize: 11, fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.3 : 1,
  outline: "none",
});

function CustomizePanel({ chipIds, colors, onChange, onClose, isMobile, counters = {} }) {
  const ref = useRef(null);
  useClickOutside(ref, onClose);
  const selected = chipIds.map((id) => CATALOG.find((c) => c.id === id)).filter(Boolean);
  const available = CATALOG.filter((c) => !chipIds.includes(c.id));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const move = (from, to) => {
    if (to < 0 || to >= chipIds.length || from === to) return;
    const next = chipIds.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange({ chipIds: next, colors });
  };
  const remove = (id) => onChange({ chipIds: chipIds.filter((x) => x !== id), colors });
  const add = (id) => {
    if (chipIds.length >= MAX_CHIPS) return;
    // Don't add a counter the backend can't fill — it would be hidden from the
    // strip and look like "nothing happened".
    if (!(counters[id] && counters[id].ok)) return;
    onChange({ chipIds: [...chipIds, id], colors });
  };
  const setColor = (id, color) => onChange({ chipIds, colors: { ...colors, [id]: color } });
  const handleDrop = (targetId) => {
    if (!dragId || targetId === dragId) return;
    move(chipIds.indexOf(dragId), chipIds.indexOf(targetId));
    setDragId(null); setOverId(null);
  };

  const panelStyle = isMobile
    ? { position: "absolute", top: 56, left: 12, right: 12, zIndex: 30 }
    : { position: "absolute", top: 56, right: 12, zIndex: 30, width: 340 };

  return (
    <div ref={ref} style={{
      ...panelStyle,
      backgroundColor: T.surface.card,
      border: "1px solid " + T.border.subtle,
      borderRadius: 12,
      boxShadow: "0 12px 32px rgba(31,42,63,0.14)",
      fontFamily: T.font.sans,
      overflow: "hidden",
    }}>
      <div style={{ padding: "12px 14px", borderBottom: "1px solid " + T.border.subtle }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Customize Live Now</span>
          <button type="button" onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 6,
            backgroundColor: "transparent", border: "none",
            color: T.text.secondary, fontSize: 16,
            cursor: "pointer", outline: "none",
          }}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>
          Pick up to {MAX_CHIPS} counters. Auto-saved on every change.
        </div>
      </div>

      <div style={{ padding: 8, maxHeight: 320, overflowY: "auto" }}>
        {selected.length === 0 ? (
          <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: T.text.muted }}>
            No counters selected. Add one below.
          </div>
        ) : selected.map((c, idx) => {
          const color = colors[c.id] || c.color;
          const isDragging = dragId === c.id;
          const isOver = overId === c.id && dragId && dragId !== c.id;
          return (
            <div
              key={c.id}
              draggable={!isMobile}
              onDragStart={(e) => {
                if (isMobile) return;
                e.dataTransfer.effectAllowed = "move";
                setDragId(c.id);
              }}
              onDragOver={(e) => {
                if (isMobile || !dragId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (c.id !== dragId) setOverId(c.id);
              }}
              onDragLeave={() => { if (overId === c.id) setOverId(null); }}
              onDrop={(e) => {
                if (isMobile) return;
                e.preventDefault();
                handleDrop(c.id);
              }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              style={{
                display: "flex", flexDirection: "column", gap: 8,
                padding: "10px 10px",
                borderRadius: 8,
                backgroundColor: T.surface.canvas,
                marginBottom: 6,
                borderLeft: "3px solid " + color,
                cursor: isMobile ? "default" : (isDragging ? "grabbing" : "grab"),
                opacity: isDragging ? 0.4 : 1,
                outline: isOver ? "2px solid " + T.brand.base : "none",
                outlineOffset: isOver ? "1px" : 0,
                transition: "opacity 120ms ease, outline-color 120ms ease",
                userSelect: "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: T.text.muted, display: "inline-flex", cursor: isMobile ? "default" : "grab" }} aria-label={isMobile ? undefined : "Drag to reorder"}><GripIcon /></span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text.primary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                <button type="button" onClick={() => remove(c.id)} aria-label="Remove" style={{
                  ...miniBtn(false), color: "#FF4757", flexShrink: 0,
                }}>✕</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <ColorSwatches value={color} onChange={(v) => setColor(c.id, v)} />
                {isMobile && (
                  <span style={{ display: "flex", gap: 6 }}>
                    <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0} aria-label="Move up" style={miniBtn(idx === 0)}>▲</button>
                    <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === selected.length - 1} aria-label="Move down" style={miniBtn(idx === selected.length - 1)}>▼</button>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: "10px 14px 6px", borderTop: "1px solid " + T.border.subtle }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted, marginBottom: 6 }}>
          Available {chipIds.length >= MAX_CHIPS && "(cap reached)"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingBottom: 10 }}>
          {available.map((c) => {
            const liveOk = counters[c.id]?.ok === true;
            const capped = chipIds.length >= MAX_CHIPS;
            const disabled = capped || !liveOk;
            return (
              <button key={c.id} type="button"
                disabled={disabled}
                title={!liveOk ? "Needs live presence/session tracking — coming soon" : undefined}
                onClick={() => { if (!disabled) add(c.id); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 10px",
                  fontSize: 11, fontWeight: 700,
                  color: disabled ? T.text.muted : T.text.primary,
                  background: T.surface.card,
                  border: "1px solid " + T.border.subtle,
                  borderRadius: 999,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: !liveOk ? 0.55 : 1,
                  outline: "none", fontFamily: T.font.sans,
                }}>
                + {c.label}
                {!liveOk && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: "0.04em",
                    textTransform: "uppercase", color: T.text.muted,
                    border: "1px solid " + T.border.subtle, borderRadius: 4,
                    padding: "1px 4px",
                  }}>soon</span>
                )}
              </button>
            );
          })}
          {available.length === 0 && (
            <span style={{ fontSize: 12, color: T.text.muted }}>All counters added.</span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 14px", borderTop: "1px solid " + T.border.subtle, backgroundColor: T.surface.canvas }}>
        <button type="button" onClick={() => onChange({ chipIds: DEFAULTS.slice(), colors: {} })} style={{
          fontSize: 11, fontWeight: 700, color: T.text.secondary,
          background: "transparent", border: "none", cursor: "pointer", outline: "none",
          padding: "4px 6px", fontFamily: T.font.sans,
        }}>Reset to defaults</button>
      </div>
    </div>
  );
}

// Briefly flash a card when its value changes — gives a tactile sense that data
// is live. On hover/focus, a tooltip shows the breakdown (e.g. signups →
// fans / creators / brands) from the counter's `detail` array.
function StatCard({ chip, value, color, fresh, detail }) {
  const [flash, setFlash] = useState(false);
  const [hover, setHover] = useState(false);
  const lastValRef = useRef(value);
  useEffect(() => {
    if (lastValRef.current !== value) {
      lastValRef.current = value;
      setFlash(true);
      const id = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [value]);
  const hasDetail = Array.isArray(detail) && detail.length > 0;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      tabIndex={hasDetail ? 0 : undefined}
      style={{
        position: "relative",
        display: "flex", flexDirection: "column", gap: 4,
        padding: "10px 14px",
        backgroundColor: T.surface.canvas,
        border: "1px solid " + T.border.subtle,
        borderLeft: "3px solid " + color,
        borderRadius: 10,
        minWidth: 0, flex: "1 1 140px",
        outline: "none",
        transition: "background-color 240ms ease",
        ...(flash ? { backgroundColor: color + "1f" } : {}),
      }}>
      <div style={{
        fontSize: 18, fontWeight: 800, lineHeight: 1.1,
        color, letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        opacity: fresh ? 1 : 0.55,
      }}>{formatValue(value, chip.format)}</div>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", color: T.text.muted,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{chip.label}</div>

      {hover && hasDetail && (
        <div role="tooltip" style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: 0,
          minWidth: 170, maxWidth: 250,
          backgroundColor: T.surface.card,
          border: "1px solid " + T.border.subtle,
          borderRadius: 10,
          boxShadow: "0 12px 32px rgba(31,42,63,0.16)",
          padding: "8px 10px", zIndex: 40,
          fontFamily: T.font.sans,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
            textTransform: "uppercase", color: T.text.muted, marginBottom: 6,
          }}>{chip.label} · breakdown</div>
          {detail.map((d, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, padding: "2px 0",
            }}>
              <span style={{ fontSize: 12, color: T.text.secondary, fontWeight: 600 }}>{d.label}</span>
              <span style={{ fontSize: 12, color: T.text.primary, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {formatValue(d.value, d.format || chip.format)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Note: legacy `items` prop is ignored. Kept for compatibility with callers.
// eslint-disable-next-line no-unused-vars
export function LiveStrip({ items }) {
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [editing, setEditing] = useState(false);
  const counters = useFakeLive();
  const { isMobile } = useViewport();

  const update = (next) => { setPrefs(next); savePrefs(next); };

  const visibleChips = prefs.chipIds
    .map((id) => CATALOG.find((c) => c.id === id))
    .filter((c) => c && counters[c.id] && counters[c.id].ok);

  const allOk = visibleChips.length > 0 && visibleChips.every((c) => counters[c.id] && counters[c.id].ok);

  return (
    <div style={{
      position: "relative",
      padding: isMobile ? 12 : 16,
      backgroundColor: T.surface.card,
      borderRadius: 16,
      fontFamily: T.font.sans,
      marginBottom: 12,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
    }}>
      {/* Header row */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, marginBottom: 10,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
          textTransform: "uppercase", color: T.text.secondary,
          display: "inline-flex", alignItems: "center",
        }}>
          <PulseDot color={allOk ? T.status.good : "#FFC107"} />
          Live now
        </span>
        <button type="button" aria-label="Customize Live Now" onClick={() => setEditing((v) => !v)} style={{
          width: 28, height: 28, borderRadius: 8,
          backgroundColor: editing ? T.brand.soft : "transparent",
          border: "1px solid " + (editing ? T.brand.base : T.border.subtle),
          color: editing ? T.brand.base : T.text.secondary,
          cursor: "pointer", outline: "none",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <PencilIcon />
        </button>
      </div>

      {/* Chip grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "repeat(2, minmax(0, 1fr))"
          : "repeat(auto-fit, minmax(min(160px, 100%), 1fr))",
        gap: 8,
      }}>
        {visibleChips.length === 0 ? (
          <span style={{
            gridColumn: "1 / -1",
            fontSize: 12, color: T.text.muted, fontWeight: 500,
            padding: "10px 12px", textAlign: "center",
          }}>
            No counters added — click the pencil to add some.
          </span>
        ) : visibleChips.map((c) => {
          const value = counters[c.id].value;
          const color = prefs.colors[c.id] || c.color;
          return (
            <StatCard key={c.id} chip={c} value={value} color={color} fresh={counters[c.id].ok} detail={counters[c.id].detail} />
          );
        })}
      </div>

      {editing && (
        <CustomizePanel
          chipIds={prefs.chipIds}
          colors={prefs.colors}
          onChange={update}
          onClose={() => setEditing(false)}
          isMobile={isMobile}
          counters={counters}
        />
      )}
    </div>
  );
}
